import type { PublicSubPortal, PublicWorkOrder, SubUploadKind } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { errorMessage, get, post } from "../api";
import { Alert, Badge, Button, Field, Loading, cx, formatDateTime } from "../components/ui";
import { WorkOrderCard } from "./PublicWorkOrder";

const STATUS: Record<string, string> = { sent: "Needs your answer", proposed: "New time proposed", confirmed: "Confirmed", declined: "Declined", completed: "Done", canceled: "Canceled" };

/** A sub's portal: every work order of theirs, answers, and uploads. No account: the link is the key. */
export function SubPortalPage() {
  const token = window.location.pathname.split("/")[2] ?? "";
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["sub-portal", token], queryFn: () => get<PublicSubPortal>(`/api/public/sub/${token}`), retry: false });
  const [open, setOpen] = useState<string | null>(null);
  useEffect(() => {
    if (q.data?.company.primaryColor) document.documentElement.style.setProperty("--nmt-primary", q.data.company.primaryColor);
  }, [q.data?.company.primaryColor]);
  if (q.isLoading)
    return (
      <div className="grid min-h-dvh place-items-center">
        <Loading />
      </div>
    );
  if (q.error || !q.data)
    return (
      <div className="mx-auto max-w-lg px-4 py-16">
        <Alert>{errorMessage(q.error) || "This link isn't valid."}</Alert>
      </div>
    );
  const p = q.data;
  const refresh = () => qc.invalidateQueries({ queryKey: ["sub-portal", token] });
  const replace = (id: string, d: PublicWorkOrder) => qc.setQueryData<PublicSubPortal>(["sub-portal", token], (x) => (x ? { ...x, workOrders: x.workOrders.map((w) => (w.id === id ? { ...d, id } : w)) } : x));
  const open_ = p.workOrders.filter((w) => !["completed", "canceled", "declined"].includes(w.status));
  const past = p.workOrders.filter((w) => !open_.includes(w));
  return (
    <div className="min-h-dvh bg-paper">
      <header className="bg-[var(--color-sidebar)] text-white">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-4 py-4">
          <div className="flex items-center gap-3">
            {p.company.logoUrl && <img src={p.company.logoUrl} alt="" className="h-9 w-auto rounded bg-white p-1" />}
            <span className="text-lg font-semibold">{p.company.name}</span>
          </div>
          <a href={`tel:${p.company.phone}`} className="text-sm text-white/80 hover:text-white">
            {p.company.phone}
          </a>
        </div>
      </header>
      <main className="mx-auto flex max-w-3xl flex-col gap-5 px-4 py-6">
        <div>
          <h1 className="text-2xl font-semibold">{p.subName}</h1>
          <p className="text-ink-soft">Your work orders with {p.company.name}. Keep this link private: it's your key.</p>
        </div>

        <section className="flex flex-col gap-3 rounded-[var(--radius-panel)] bg-white px-5 py-4 text-sm shadow-[var(--shadow-card)]">
          <div className="font-semibold">Your paperwork</div>
          {p.paperwork.length ? (
            <ul className="list-disc pl-5 text-ink-soft">
              {p.paperwork.map((x) => (
                <li key={x}>{x}</li>
              ))}
            </ul>
          ) : (
            <p className="text-ink-soft">All current. Thanks!</p>
          )}
          <Upload token={token} kind="certificate" workOrderId={null} label="Send a new certificate or W-9" multiple max={5} onDone={refresh} />
        </section>

        <h2 className="text-lg font-semibold">Open work orders</h2>
        {!open_.length && <p className="text-sm text-muted">Nothing open right now.</p>}
        {open_.map((w) => (
          <Item key={w.id} token={token} w={w} expanded={open === w.id || open_.length === 1} onToggle={() => setOpen(open === w.id ? null : w.id)} onDone={(d) => replace(w.id, d)} onUploaded={refresh} />
        ))}
        {!!past.length && (
          <>
            <h2 className="text-lg font-semibold">Recent</h2>
            {past.map((w) => (
              <Item key={w.id} token={token} w={w} expanded={open === w.id} onToggle={() => setOpen(open === w.id ? null : w.id)} onDone={(d) => replace(w.id, d)} onUploaded={refresh} />
            ))}
          </>
        )}
      </main>
    </div>
  );
}

function Item({ token, w, expanded, onToggle, onDone, onUploaded }: { token: string; w: PublicSubPortal["workOrders"][number]; expanded: boolean; onToggle: () => void; onDone: (d: PublicWorkOrder) => void; onUploaded: () => void }) {
  return (
    <div className="flex flex-col gap-3">
      <button onClick={onToggle} className="flex flex-wrap items-center gap-3 rounded-[var(--radius-panel)] bg-white px-5 py-3 text-left shadow-[var(--shadow-card)] hover:bg-mist/40">
        <Badge tone={w.status === "sent" || w.status === "proposed" ? "brand" : w.status === "confirmed" ? "ok" : "off"}>{STATUS[w.status] ?? w.status}</Badge>
        <span className="font-semibold">
          Work order {w.number}
          {w.title && ` · ${w.title}`}
        </span>
        <span className="text-sm text-muted">{[w.startsAt ? formatDateTime(w.startsAt) : "Not scheduled", w.address].filter(Boolean).join(" · ")}</span>
      </button>
      {expanded && (
        <div className="flex flex-col gap-4 pl-2">
          <WorkOrderCard w={w} respondUrl={`/api/public/sub/${token}/work-orders/${w.id}/respond`} onDone={onDone} />
          {["confirmed", "completed"].includes(w.status) && (
            <section className="flex flex-col gap-3 rounded-[var(--radius-panel)] bg-white px-5 py-4 text-sm shadow-[var(--shadow-card)]">
              <div className="font-semibold">Send us</div>
              <Upload token={token} kind="photo" workOrderId={w.id} label="Photos of the work" multiple accept="image/*" onDone={onUploaded} />
              <Upload token={token} kind="invoice" workOrderId={w.id} label="Your invoice" multiple max={5} onDone={onUploaded} />
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function Upload({ token, kind, workOrderId, label, multiple, max, accept, onDone }: { token: string; kind: SubUploadKind; workOrderId: string | null; label: string; multiple?: boolean; /** Most files at once. */ max?: number; accept?: string; onDone: () => void }) {
  const input = useRef<HTMLInputElement>(null);
  const [note, setNote] = useState("");
  const [done, setDone] = useState<string | null>(null);
  const [tooMany, setTooMany] = useState<string | null>(null);
  const send = useMutation({
    mutationFn: async (files: File[]) => {
      const slots = await post<{ key: string; url: string; headers: Record<string, string> }[]>(`/api/public/sub/${token}/upload-urls`, { kind, workOrderId, files: files.map((f) => ({ name: f.name, contentType: f.type || "application/octet-stream", size: f.size })) });
      for (const [i, f] of files.entries()) {
        const res = await fetch(slots[i]!.url, { method: "PUT", headers: slots[i]!.headers, body: f });
        if (!res.ok) throw new Error("Upload failed. Try again.");
        await post(`/api/public/sub/${token}/files`, { kind, workOrderId, key: slots[i]!.key, name: f.name, contentType: f.type || "application/octet-stream", note });
      }
      return files.length;
    },
    onSuccess: (n) => {
      setDone(`Sent ${n} file${n === 1 ? "" : "s"}. Thank you!`);
      setNote("");
      onDone();
    },
  });
  return (
    <div className={cx("flex flex-col gap-2 border-t border-mist pt-3 first:border-0 first:pt-0")}>
      <div className="flex flex-wrap items-end gap-2">
        <Field className="min-w-48 flex-1" label={`${label}: note (optional)`} value={note} maxLength={500} onChange={(e) => setNote(e.target.value)} />
        <input
          ref={input}
          type="file"
          multiple={multiple}
          accept={accept ?? "image/*,application/pdf"}
          className="hidden"
          onChange={(e) => {
            const files = [...(e.target.files ?? [])];
            setTooMany(max && files.length > max ? `Up to ${max} files at a time. Pick ${max} or fewer.` : null);
            if (files.length && (!max || files.length <= max)) send.mutate(files);
            e.target.value = "";
          }}
        />
        <Button busy={send.isPending} onClick={() => (setDone(null), input.current?.click())}>
          Choose {multiple ? (max ? `files (up to ${max})` : "files") : "a file"}
        </Button>
      </div>
      {tooMany && <Alert>{tooMany}</Alert>}
      {send.error && <Alert>{errorMessage(send.error)}</Alert>}
      {done && <Alert tone="ok">{done}</Alert>}
    </div>
  );
}
